import { Info, Sparkles, Wand2, FileText, Check } from "lucide-react";
import { useState } from "react";
import { useT } from "@/lib/i18n";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * Trust-indicator next to the big Match-score badge.
 *
 * Opens a popover that explains how the AI derives the score — the same
 * weighting + score anchors that live in `letter_ai.py`. When a `score` is
 * supplied, the current band is highlighted and a contextual CTA is shown:
 *  - 90-100 → "letter ready to send" congrats.
 *  - 40-89  → "Start Match-Verbeter Wizard" (via onOpenWizard callback).
 *  - < 40   → hint to pick a better CV variant (no direct action — user needs
 *              to leave the sheet).
 */
export function MatchScoreExplainer({ score = null, onOpenWizard = null, onNavigateToCvs = null }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);

  const bandKey = (s) => {
    if (s === null || s === undefined) return null;
    if (s >= 90) return "b90";
    if (s >= 75) return "b75";
    if (s >= 60) return "b60";
    if (s >= 40) return "b40";
    return "bLow";
  };
  const currentBand = bandKey(score);

  const bands = [
    { key: "b90",  range: "90–100", accent: "text-emerald-700", label: t("mseBand90") },
    { key: "b75",  range: "75–89",  accent: "text-emerald-700", label: t("mseBand75") },
    { key: "b60",  range: "60–74",  accent: "text-amber-700",   label: t("mseBand60") },
    { key: "b40",  range: "40–59",  accent: "text-orange-700",  label: t("mseBand40") },
    { key: "bLow", range: "< 40",   accent: "text-rose-700",    label: t("mseBandLow") },
  ];

  const showWizardCta = currentBand === "b75" || currentBand === "b60" || currentBand === "b40";
  const showChooseCvHint = currentBand === "bLow";
  const showCongrats = currentBand === "b90";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="match-score-explainer-trigger"
          type="button"
          onClick={(e) => e.stopPropagation()}
          title={t("mseTrigger")}
          className="ml-1 inline-flex items-center justify-center w-6 h-6 border border-neutral-300 text-neutral-500 hover:border-neutral-900 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
        >
          <Info size={12} strokeWidth={1.75} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        data-testid="match-score-explainer"
        align="start"
        sideOffset={8}
        className="!w-[22rem] !bg-[#F9F8F6] !border-neutral-900 !rounded-none !p-0"
      >
        <div className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={14} strokeWidth={1.5} className="text-amber-500" />
            <h4 className="font-serif text-lg leading-tight">{t("mseTitle")}</h4>
          </div>

          <p className="text-sm text-neutral-700 leading-relaxed mb-4">{t("mseIntro")}</p>

          <p className="ed-eyebrow mb-2">{t("mseWeightingTitle")}</p>
          <ul className="space-y-1.5 mb-4">
            <li className="flex items-baseline gap-2 text-sm">
              <span className="font-mono text-[0.65rem] text-neutral-500 min-w-[3rem]">50%</span>
              <span className="text-neutral-800">{t("mseWeightMust")}</span>
            </li>
            <li className="flex items-baseline gap-2 text-sm">
              <span className="font-mono text-[0.65rem] text-neutral-500 min-w-[3rem]">30%</span>
              <span className="text-neutral-800">{t("mseWeightTasks")}</span>
            </li>
            <li className="flex items-baseline gap-2 text-sm">
              <span className="font-mono text-[0.65rem] text-neutral-500 min-w-[3rem]">20%</span>
              <span className="text-neutral-800">{t("mseWeightSoft")}</span>
            </li>
          </ul>

          <p className="ed-eyebrow mb-2">{t("mseBandsTitle")}</p>
          <ul className="space-y-1 mb-4 text-sm">
            {bands.map((b) => {
              const active = currentBand === b.key;
              return (
                <li
                  key={b.key}
                  data-testid={`mse-band-${b.key}${active ? "-active" : ""}`}
                  className={active
                    ? "flex items-start gap-2 -mx-1 px-1 py-0.5 bg-neutral-900/5 border-l-2 border-neutral-900 font-medium"
                    : "flex items-start gap-2"
                  }
                >
                  <span className={`inline-block w-14 font-mono text-[0.65rem] ${b.accent}`}>{b.range}</span>
                  <span>{b.label}</span>
                </li>
              );
            })}
          </ul>

          {/* Contextual CTA based on the current band. */}
          {showWizardCta && onOpenWizard && (
            <button
              data-testid="mse-cta-wizard"
              type="button"
              onClick={() => { setOpen(false); onOpenWizard(); }}
              className="ed-btn w-full justify-center mb-3"
            >
              <Wand2 size={13} strokeWidth={1.75} /> {t("mseCtaWizard")}
            </button>
          )}
          {showChooseCvHint && (
            <div
              data-testid="mse-cta-choose-cv"
              className="mb-3 p-3 border border-rose-700 bg-rose-50 text-rose-900 text-xs leading-relaxed flex items-start gap-2"
            >
              <FileText size={13} strokeWidth={1.75} className="shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium mb-0.5">{t("mseCtaChooseCv")}</p>
                <p className="mb-2">{t("mseCtaChooseCvHint")}</p>
                {onNavigateToCvs && (
                  <button
                    data-testid="mse-cta-choose-cv-btn"
                    type="button"
                    onClick={() => { setOpen(false); onNavigateToCvs(); }}
                    className="font-mono text-[0.6rem] uppercase tracking-[0.15em] border border-rose-800 bg-white text-rose-900 hover:bg-rose-800 hover:text-white transition-colors px-2.5 py-1 inline-flex items-center gap-1.5"
                  >
                    <FileText size={11} strokeWidth={1.75} /> {t("mseCtaChooseCvBtn")}
                  </button>
                )}
              </div>
            </div>
          )}
          {showCongrats && (
            <div
              data-testid="mse-cta-congrats"
              className="mb-3 p-3 border border-emerald-700 bg-emerald-50 text-emerald-900 text-xs leading-relaxed flex items-start gap-2"
            >
              <Check size={13} strokeWidth={1.75} className="shrink-0 mt-0.5" />
              <p>{t("mseCtaCongrats")}</p>
            </div>
          )}

          <div className="border-t border-neutral-200 pt-3">
            <p className="text-xs font-mono text-neutral-500 leading-relaxed">
              {t("mseDisclaimer")}
            </p>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
